import React, { useState } from 'react';
import { useLoaderData, useParams } from 'react-router-dom';
import { redirectErrorPage } from '../../utils/errors';
import useDimensions from '../../hooks/useDimensions';
import ChartComponent from '../../components/ChartComponent';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from '../../utils/texts';
import MemberCardWrapper from '../../components/common/MemberCardWrapper/MemberCardWrapper';
import Intro from '../../components/common/Intro/Intro';
import SummarySection from '../../components/common/SummarySection/SummarySection';
import { Colors } from '../../Constants';
import { format } from 'date-fns';
import { cs, enUS } from 'date-fns/locale';
import Section from '../../components/common/Section/Section';
import { SummarySectionListItemType } from '../../types/SummarySectionListItemType';

type ResponseMemberData = { clen_id: number; clen_jmeno: string; clen_prijmeni: string; clen_pohlavi: 0 | 1 };

type AttendanceData = {
    cislo_zasedani: string;
    sumarizace: {
        přítomnost: number;
        'částečná přítomnost': number;
        nepřítomnost: number;
    };
    prehled: {
        strana_zkratka: string;
        hodnoty: number[];
        strana_barva: string;
    }[];
    detail: {
        strana_id: number;
        strana_zkratka: string;
        strana_barva: string;
        [key: string]: any;
    }[][];
};

type MemberGroup = {
    count: number;
    partyColor: string;
    partyName: string;
    partyId: number;
    members: any;
};

type FormattedData = {
    přítomnost: { count: number; memberGroups: MemberGroup[] };
    částečná_přítomnost: { count: number; memberGroups: MemberGroup[] };
    nepřítomnost: { count: number; memberGroups: MemberGroup[] };
};

function MeetingDetail() {
    const [activeTab, setActiveTab] = useState<null | number>(null);
    const { width } = useDimensions();

    const { t, i18n } = useTranslation();
    const currentLocale = i18n.language;

    const { municipalitaId, zastupitelstvoId } = useParams();

    // Typed as any for now, should be fixed in the future when types are generated on BE
    const response: any = useLoaderData();
    const sessionData = response[0].data;
    const attendanceData: AttendanceData = response[1].data;

    // TMP, should be done on BE
    const formatAttendanceData = (attendanceData: AttendanceData) => {
        const formattedData: FormattedData = {
            přítomnost: { count: 0, memberGroups: [] },
            částečná_přítomnost: { count: 0, memberGroups: [] },
            nepřítomnost: { count: 0, memberGroups: [] }
        };

        const categories = {
            přítomnost: 0,
            částečná_přítomnost: 1,
            nepřítomnost: 2
        };

        const findPartyDetail = (partyAbreviation: string, categoryIndex: number) => {
            return attendanceData.detail[categoryIndex].find((party) => party.strana_zkratka === partyAbreviation);
        };

        attendanceData.prehled.forEach((party) => {
            const partyDetailPresent = findPartyDetail(party.strana_zkratka, 0);
            const partyDetailPartial = findPartyDetail(party.strana_zkratka, 1);
            const partyDetailAbsent = findPartyDetail(party.strana_zkratka, 2);

            if (partyDetailPresent) {
                const partyData = {
                    count: party.hodnoty[categories.přítomnost],
                    partyColor: party.strana_barva,
                    partyName: party.strana_zkratka,
                    partyId: partyDetailPresent.strana_id,
                    members:
                        partyDetailPresent.clenove.map((member: ResponseMemberData) => ({
                            id: member.clen_id,
                            firstName: member.clen_jmeno,
                            lastName: member.clen_prijmeni,
                            gender: member.clen_pohlavi
                        })) || []
                };

                if (partyData.count > 0) {
                    formattedData.přítomnost.memberGroups.push(partyData);
                    formattedData.přítomnost.count += partyData.count;
                }
            }

            if (partyDetailPartial) {
                const partialPresenceData = {
                    count: party.hodnoty[categories.částečná_přítomnost],
                    partyColor: party.strana_barva,
                    partyName: party.strana_zkratka,
                    partyId: partyDetailPartial.strana_id,
                    members:
                        partyDetailPartial.clenove.map((member: ResponseMemberData) => ({
                            id: member.clen_id,
                            firstName: member.clen_jmeno,
                            lastName: member.clen_prijmeni,
                            gender: member.clen_pohlavi
                        })) || []
                };

                if (partialPresenceData.count > 0) {
                    formattedData.částečná_přítomnost.memberGroups.push(partialPresenceData);
                    formattedData.částečná_přítomnost.count += partialPresenceData.count;
                }
            }

            if (partyDetailAbsent) {
                const absenceData = {
                    count: party.hodnoty[categories.nepřítomnost],
                    partyColor: party.strana_barva,
                    partyName: party.strana_zkratka,
                    partyId: partyDetailAbsent.strana_id,
                    members:
                        partyDetailAbsent.clenove.map((member: ResponseMemberData) => ({
                            id: member.clen_id,
                            firstName: member.clen_jmeno,
                            lastName: member.clen_prijmeni,
                            gender: member.clen_pohlavi
                        })) || []
                };

                if (absenceData.count > 0) {
                    formattedData.nepřítomnost.memberGroups.push(absenceData);
                    formattedData.nepřítomnost.count += absenceData.count;
                }
            }
        });

        (Object.keys(formattedData) as (keyof FormattedData)[]).forEach((key) => {
            if (formattedData[key].count === 0) {
                delete formattedData[key];
            }
        });

        return formattedData;
    };

    const formattedAttendanceData: FormattedData = formatAttendanceData(attendanceData);

    if (sessionData !== undefined && attendanceData !== undefined) {
        if (
            JSON.stringify(sessionData) === '{"error":"neexistujici ID"}' ||
            JSON.stringify(attendanceData) === '{"error":"neexistujici ID"}'
        ) {
            redirectErrorPage(municipalitaId, zastupitelstvoId);
        } else {
            let datasetsDataSummaryAttendanceTmp = new Map();

            (Object.keys(formattedAttendanceData) as (keyof FormattedData)[]).forEach((key) => {
                formattedAttendanceData[key].memberGroups.forEach((group) => {
                    if (!datasetsDataSummaryAttendanceTmp.has(group.partyName)) {
                        datasetsDataSummaryAttendanceTmp.set(group.partyName, {
                            type: 'bar',
                            label: group.partyName,
                            backgroundColor: group.partyColor,
                            data: [
                                formattedAttendanceData.přítomnost?.memberGroups.find(
                                    (g) => g.partyName === group.partyName
                                )?.count || 0,
                                formattedAttendanceData.částečná_přítomnost?.memberGroups.find(
                                    (g) => g.partyName === group.partyName
                                )?.count || 0,
                                formattedAttendanceData.nepřítomnost?.memberGroups.find(
                                    (g) => g.partyName === group.partyName
                                )?.count || 0
                            ]
                        });
                    }
                });
            });

            let datasetsDataSummaryAttendance = Array.from(datasetsDataSummaryAttendanceTmp.values());

            let summaryAttendanceData = {
                labels: [
                    'přítomnost (' + attendanceData['sumarizace']['přítomnost'] + ')',
                    ['částečná', 'přítomnost (' + attendanceData['sumarizace']['částečná přítomnost'] + ')'],
                    'nepřítomnost (' + attendanceData['sumarizace']['nepřítomnost'] + ')'
                ],
                datasets: datasetsDataSummaryAttendance
            };

            const stackedOptions = {
                indexAxis: width <= 576 ? 'x' : 'y',
                maintainAspectRatio: false,
                aspectRatio: 0.8,
                plugins: {
                    tooltips: {
                        mode: 'index',
                        intersect: false
                    }
                },
                scales: {
                    x: {
                        stacked: true
                        //max: maxX,
                    },
                    y: {
                        stacked: true,
                        position: 'left'
                    }
                },
                onHover: function (evt: { native: { target: { style: { cursor: string } } } }, item: undefined[]) {
                    if (item[0] !== undefined) {
                        evt.native.target.style.cursor = 'pointer';
                    } else {
                        evt.native.target.style.cursor = 'default';
                    }
                },
                onClick: function (
                    evt: any,
                    item: {
                        index: number;
                        datasetIndex: number;
                    }[]
                ) {
                    if (item[0] !== undefined) {
                        const newIndex = item[0].index;
                        const datasetIndex = item[0].datasetIndex;

                        setActiveTab(null);
                        setTimeout(() => {
                            setActiveTab(newIndex);
                            const element = document.getElementById(`${newIndex}-datasetIndex-${datasetIndex}`);
                            if (element) {
                                setTimeout(() => element?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 330);
                            }
                        }, 0);
                    }
                }
            };

            const percentageAttendanceString = `${sessionData.attendance.toFixed(2)}%`;
            const mutedAttendenceString = `(${t('x-out-of-y', {
                x: sessionData.ucast,
                y: sessionData.celkem
            })} ${t('of-members')})`;
            const summarySectionItems = [
                {
                    icon: 'group',
                    text: `${percentageAttendanceString} ${t('participation')} ${mutedAttendenceString}`,
                    highlightSubString: `${percentageAttendanceString}`,
                    mutedSubString: `${mutedAttendenceString}`
                },
                {
                    icon: 'stacks',
                    text: `${sessionData.pocet_hlasovani.platna} ${t('valid-votings')}`,
                    highlightSubString: `${sessionData.pocet_hlasovani.platna}`
                },
                {
                    icon: 'check_box',
                    text: `${sessionData.pocet_hlasovani.prijata} ${t('approved-proposals')}`,
                    nested: true,
                    highlightSubString: `${sessionData.pocet_hlasovani.prijata}`,
                    iconAndHighlightColor: Colors.success
                },
                {
                    icon: 'disabled_by_default',
                    text: `${sessionData.pocet_hlasovani.neprijata}
                    ${t('rejected-proposals', {
                        ns: 'translation'
                    })}`,
                    nested: true,
                    highlightSubString: `${sessionData.pocet_hlasovani.neprijata}`,
                    iconAndHighlightColor: Colors.error
                }
            ] as SummarySectionListItemType[];

            if (sessionData.video_zaznam[0]?.url) {
                summarySectionItems.push({
                    icon: 'videocam',
                    text: `${capitalizeFirstLetter(t('record'))} ${t('and')}${t('materials')} ${t('for-meeting')}`,
                    highlightSubString: `${capitalizeFirstLetter(t('record'))} ${t('and')}${t('materials')}`,
                    type: 'link' as 'link',
                    linkUrl: `${sessionData.video_zaznam[0].url}`
                });
            }

            const formattedDate = format(new Date(sessionData.datum.split('.').reverse().join('-')), 'd. MMMM yyyy', {
                locale: currentLocale === 'cs' ? cs : enUS
            });

            return (
                <>
                    <Intro title={`${t('meeting-no')} ${sessionData.cislo_zasedani}`} subtitle={`${formattedDate}`} />
                    <SummarySection summaryItems={summarySectionItems} contentClassName='meeting-detail-summary' />
                    <Section title={t('councilMember_attendance')} subtitle={t('tooltip-chart')}>
                        <ChartComponent
                            type='bar'
                            data={summaryAttendanceData}
                            options={stackedOptions}
                            height={width <= 576 ? '400px' : '300px'}
                            width='100%'
                            xTitle={undefined}
                            yTitle={undefined}
                            aria={undefined}
                        />
                        {Object.keys(formattedAttendanceData).map((option, index) => {
                            const options = Object.keys(formattedAttendanceData);
                            const optionIndex = options.indexOf(option);
                            return (
                                <MemberCardWrapper
                                    key={index}
                                    tabIndex={optionIndex}
                                    groupName={`${capitalizeFirstLetter(option.replace('_', ' '))}`}
                                    count={formattedAttendanceData[option as keyof FormattedData].count}
                                    memberGroupsData={
                                        formattedAttendanceData[option as keyof FormattedData].memberGroups
                                    }
                                    expanded={activeTab === optionIndex}
                                    relatedChartDataset={datasetsDataSummaryAttendance}
                                />
                            );
                        })}
                    </Section>
                </>
            );
        }
    }
}

export default MeetingDetail;
